/**
 * QuizResultFeedback Component
 * Ported from reference_legacy/quebon-lms-web/components/lesson/quiz-types/shared/quiz-result-feedback.tsx
 */

import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../../lib/utils'
import { CheckCircle2, Clock3, XCircle, Lightbulb } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { QUIZ_FEEDBACK_STYLES } from './quiz-styles'
import { useQuizStyleVariant } from './quiz-style-context'

// Sound assets
import correctSound from '../../../assets/sounds/correct.wav'
import incorrectSound from '../../../assets/sounds/incorrect.wav'

interface QuizResultFeedbackProps {
  isCorrect: boolean | null
  show: boolean
  correctAnswer?: string
  explanation?: string
  /** 사운드 비활성화 (리뷰 모드 등에서 사용) */
  disableSound?: boolean
  /** 검수/열람 모드 — 실제 제출이 없으므로 정답/오답 박스를 표시하지 않음 */
  isViewSolved?: boolean
}

export function QuizResultFeedback({
  isCorrect,
  show,
  correctAnswer: _correctAnswer,
  explanation,
  disableSound = false,
  isViewSolved = false,
}: QuizResultFeedbackProps) {
  const { t } = useTranslation('quiz')
  const variant = useQuizStyleVariant()
  const feedbackStyles = QUIZ_FEEDBACK_STYLES[variant]
  const hasPlayedSound = useRef(false)

  // Play sound effect when feedback is shown
  useEffect(() => {
    if (show && !isViewSolved && !hasPlayedSound.current && !disableSound && isCorrect !== null) {
      const audio = new Audio(isCorrect ? correctSound : incorrectSound)
      audio.volume = 0.5
      audio.play().catch(() => {
        // Ignore autoplay errors (browser policy)
      })
      hasPlayedSound.current = true
    }
    if (!show) {
      hasPlayedSound.current = false
    }
  }, [show, isViewSolved, isCorrect, disableSound])

  return (
    <AnimatePresence>
      {show && !isViewSolved && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="space-y-3"
          role="alert"
          aria-live="polite"
        >
          {/* 서술형: 제출 완료 (채점 없음) */}
          {isCorrect === null && (
            <div className={cn('p-4 rounded-xl flex items-center gap-3', feedbackStyles.submitted.container)}>
              <div className={cn('w-10 h-10 rounded-full flex items-center justify-center shrink-0', feedbackStyles.submitted.icon)}>
                <Clock3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className={cn('text-lg font-bold', feedbackStyles.submitted.title)}>{t('feedback.submitted', '제출 완료')}</p>
                <p className={cn('text-base', feedbackStyles.submitted.text)}>{t('feedback.submittedMessage', '선생님이 답안을 확인하고 있어요.')}</p>
              </div>
            </div>
          )}

          {/* 정답/오답 표시 - 기획 데모 스타일 */}
          {isCorrect !== null && (
            <div
              className={cn(
                'p-4 rounded-xl flex items-center gap-3',
                isCorrect ? feedbackStyles.correct.container : feedbackStyles.wrong.container
              )}
            >
              {isCorrect ? (
                <>
                  <div className={cn('w-10 h-10 rounded-full flex items-center justify-center shrink-0', feedbackStyles.correct.icon)}>
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className={cn('text-lg font-bold', feedbackStyles.correct.title)}>{t('feedback.correct')}</p>
                    <p className={cn('text-base', feedbackStyles.correct.text)}>{t('feedback.correctMessage')}</p>
                  </div>
                </>
              ) : (
                <>
                  <div className={cn('w-10 h-10 rounded-full flex items-center justify-center shrink-0', feedbackStyles.wrong.icon)}>
                    <XCircle className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className={cn('text-lg font-bold', feedbackStyles.wrong.title)}>{t('feedback.incorrect')}</p>
                  </div>
                </>
              )}
            </div>
          )}

          {explanation && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="p-4 rounded-2xl bg-linear-to-r from-gray-50 to-slate-50 border border-gray-200"
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center shrink-0">
                  <Lightbulb className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-lg font-semibold text-gray-700 mb-1">{t('feedback.explanation')}</p>
                  <p className="text-base text-gray-600 leading-relaxed">{explanation}</p>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
